import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useBatchTiming } from "../hooks/useBatchTiming";
import { useCourse } from "../../course/hooks/useCourse";
import BatchTimingModals from "../BatchTimingModals";
import { BatchTimingResponseType } from "../types/batchTimingResponse";
import Table from "../../../../core/common/dataTable/index";
import PredefinedDateRanges from "../../../../core/common/datePicker";
import { all_routes } from "../../../router/all_routes";
import { useGetAllBatchTimingQuery } from "../api/batchTimingApi";

const BatchTimingList: React.FC = () => {
  const routes = all_routes;
  const { getCourseState } = useCourse();
  const { courses } = getCourseState();
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedBatchTimingId, setSelectedBatchTimingId] =
    useState<string>("");

  const { data, isLoading, error } = useGetAllBatchTimingQuery({
    page: pageNumber,
    size: recordsPerPage,
    search: search,
  });

  const batchTimings = data?.data?.items || [];
  const totalItems = data?.data?.totalItems || 0;

  const handleSearchChange = (text: string) => {
    setSearch(text);
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setPageNumber(page);
    if (pageSize) setRecordsPerPage(pageSize);
  };

  const columns = [
    {
      title: "S. No",
      render: (_: any, __: any, index: number) => index + 1,
      sorter: (a: any, b: any) => a.id - b.id,
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a: BatchTimingResponseType, b: BatchTimingResponseType) =>
        a.name.localeCompare(b.name),
    },
    {
      title: "Course",
      dataIndex: "course_id",
      render: (courseId: number) => {
        const course = courses.find((c: any) => c.id === courseId);
        return course ? course.name : "N/A";
      },
      sorter: (a: BatchTimingResponseType, b: BatchTimingResponseType) => {
        const courseA = courses.find((c: any) => c.id === a.course_id);
        const courseB = courses.find((c: any) => c.id === b.course_id);
        const nameA = courseA ? courseA.name : "";
        const nameB = courseB ? courseB.name : "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Level",
      dataIndex: "stage_id",
      render: (stageId: number, record: BatchTimingResponseType) => {
        const course = courses.find((c: any) => c.id === record.course_id);
        const stage = course?.courseStages?.find((s: any) => s.id === stageId);
        return stage ? stage.name : "N/A";
      },
    },
    {
      title: "Days",
      dataIndex: "days",
      render: (days: string[]) => {
        if (days && days.length <= 3) {
          return days.join(", ");
        } else if (days && days.length > 3) {
          const firstLine = days.slice(0, 3).join(", ");
          const secondLine = days.slice(3).join(", ");
          return (
            <div>
              <div>{firstLine}</div>
              <div>{secondLine}</div>
            </div>
          );
        }
        return "N/A";
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <span
          className={`badge badge-soft-${text === "active" ? "success" : "danger"
            } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {text}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: string, record: BatchTimingResponseType) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to={routes.editBatchTiming.replace(
                    ":id",
                    record.id.toString()
                  )}
                  onClick={() => setSelectedBatchTimingId(record.id.toString())}
                >
                  <i className="ti ti-edit me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#delete-modal"
                  onClick={() => setSelectedBatchTimingId(record.id.toString())}
                >
                  <i className="ti ti-trash me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Batch Timings</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">Configuration</li>
                  <li className="breadcrumb-item active">Batch Timings</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <Link
                  to={routes.addBatchTiming}
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Batch Timing
                </Link>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Batch Timing List</h4>
              <div className="d-flex align-items-center flex-wrap">
                {/* <div className="input-icon-start mb-3 me-2 position-relative">
                  <PredefinedDateRanges />
                </div> */}
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {isLoading ? (
                <div className="text-center">Loading batch timings...</div>
              ) : error ? (
                <div className="alert alert-danger m-3">
                  Error loading batch timings: {error?.toString()}
                </div>
              ) : (
                <Table
                  dataSource={batchTimings}
                  columns={columns}
                  Selection={false}
                  onSearchChange={handleSearchChange}
                  rowKey="id"
                  pagination={{
                    current: pageNumber,
                    pageSize: recordsPerPage,
                    total: totalItems,
                    onChange: handlePageChange,
                    pageSizeOptions: [10, 20, 30],
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <BatchTimingModals batchTimingId={selectedBatchTimingId} />
    </>
  );
};

export default BatchTimingList;
